import { useEffect, useRef, useState } from 'react'

import { useAssent } from '@/entities/assent'
import { useSession } from '@/entities/session'

import { useWebPush } from '@/shared/integrations/web-push'
import { callNotification } from '@/shared/lib/call-notification/call-notification'
import { allowOnlyNumbers } from '@/shared/lib/utils/inputs/allow-only-number'

import { AuthByPhoneApi } from '../api/auth-by-phone-api'
import { getAxiosErrorMessage } from '../lib/get-axios-error-message'
import { persistAuthSession } from '../lib/persist-auth-session'
import { AuthIntent, AuthStep, StartLoginResult } from '../types/auth-flow'
import { SignInData, SignInResponseData } from '../types/auth-schema'

import { COOLDOWN_CALL_SEC, COOLDOWN_CODE_SEC, useAuthCooldown } from './use-auth-cooldown'

type UseAuthByPhoneProps = {
	onSuccess?: () => void
}

export const useAuthByPhone = (props: UseAuthByPhoneProps) => {
	const { onSuccess } = props
	const { setSessionToken } = useSession()
	const { notification, clearNotification, getPushToken, registerDevice } = useWebPush()
	const { resendCooldown, isCooldown, startCooldown } = useAuthCooldown()
	const {
		showNewsletterCheckbox,
		isAccepted: isNewsletterAccepted,
		hasConsent,
		isLoading: isAssentLoading,
		setIsAccepted: setIsNewsletterAccepted,
		checkConsent,
		saveConsent
	} = useAssent()

	const [isLoading, setIsLoading] = useState(false)
	const [step, setStep] = useState<AuthStep>('phone')
	const [phone, setPhone] = useState<string | null>(null)
	const [signInData, setSignInData] = useState<SignInData | null>(null)
	const [smsCode, setSmsCode] = useState('')
	const [smsCodeError, setSmsCodeError] = useState('')
	const [submitError, setSubmitError] = useState('')
	const [isAuthError, setIsAuthError] = useState(false)
	const intentRef = useRef<AuthIntent>('login')
	const captchaRef = useRef<string | undefined>(undefined)
	const verifyCallRequestRef = useRef<AbortController | null>(null)

	//Вызов колбеков при успешной авторизации
	const persist = (tokens: SignInResponseData, rawPhone: string) => {
		persistAuthSession({ tokens, setSessionToken, onSuccess })
		registerDevice(getPushToken(), rawPhone.replace('+', ''))
	}

	const requestLogin = (phoneNumber: string, type: 'call_user' | 'event', isReset?: boolean) =>
		AuthByPhoneApi.login({
			phone: phoneNumber,
			captcha: captchaRef.current,
			deviceUuid: getPushToken(),
			type,
			isReset
		})

	//POST registration
	const requestSignUp = (phoneNumber: string, type: 'call_user' | 'event') =>
		AuthByPhoneApi.signUp({ phone: phoneNumber, captcha: captchaRef.current, type })

	//POST type=event в /login или /registration в зависимости от intent
	const requestSignedData = async (phoneNumber: string, isReset?: boolean) => {
		if (intentRef.current === 'register') {
			return requestSignUp(phoneNumber, 'event')
		}
		return requestLogin(phoneNumber, 'event', isReset)
	}

	//Переход на шаг push и запрос signed_data (/login или /registration)
	const goToPush = async (phoneNumber: string) => {
		setSignInData(await requestSignedData(phoneNumber))
		startCooldown(COOLDOWN_CODE_SEC)
		setStep('push')
	}

	//Переход на шаг звонка пользователя и запрос call/verify
	const goToCall = async (phoneNumber: string, data: SignInData) => {
		setSignInData(data)
		setSmsCode('')
		startCooldown(COOLDOWN_CALL_SEC)
		setStep('call')

		const controller = new AbortController()
		verifyCallRequestRef.current = controller

		const callResult = await AuthByPhoneApi.verifyCall(phoneNumber, controller.signal)
		if (callResult.status === 'aborted') return

		if (callResult.status === 'ok') {
			persist(callResult.tokens, phoneNumber)
			return
		}

		callNotification.success('Ошибка авторизации через звонок, переводим вас на другой способ авторизации')
		setStep('sms')
		setSmsCode('')

		try {
			setSignInData(await requestSignedData(phoneNumber, true))
			startCooldown(COOLDOWN_CODE_SEC)
		} catch (error) {
			setSmsCodeError(getAxiosErrorMessage(error, 'Не удалось отправить SMS-код. Попробуйте ещё раз'))
		}
	}

	//login/check: капча, is_new_user → registration, иначе /login → call или push
	const startLogin = async (phoneNumber: string, captchaToken?: string): Promise<StartLoginResult | undefined> => {
		setPhone(phoneNumber)
		intentRef.current = 'login'
		captchaRef.current = captchaToken
		setSubmitError('')
		setIsLoading(true)

		try {
			const check = await AuthByPhoneApi.checkPhone(phoneNumber)
			if (check.data.show_captcha && !captchaToken) {
				return { status: 'captcha_required' }
			}
			if (check.data.is_new_user) {
				await checkConsent(phoneNumber)
				setStep('registration')
				return
			}

			const data = await requestLogin(phoneNumber, 'call_user')
			if (data.TYPE_AUTH === 'push') {
				await goToPush(phoneNumber)
				return
			}
			await goToCall(phoneNumber, data)
		} catch (error) {
			return {
				status: 'error',
				message: getAxiosErrorMessage(error, 'У нас что-то сломалось. Попробуйте авторизоваться позже')
			}
		} finally {
			setIsLoading(false)
		}
	}

	//Принятие политик при регистрации и переход в call или push в зависимости от ответа /registration
	const confirmRegistration = async () => {
		if (!phone) return
		intentRef.current = 'register'
		setSubmitError('')
		setIsLoading(true)

		try {
			const data = await requestSignUp(phone, 'call_user')
			if (!hasConsent && isNewsletterAccepted) {
				await saveConsent(phone)
			}
			if (data.TYPE_AUTH === 'push') {
				await goToPush(phone)
				return
			}
			await goToCall(phone, data)
		} catch (error) {
			setSubmitError(getAxiosErrorMessage(error, 'У нас что-то сломалось. Попробуйте зарегистрироваться позже'))
		} finally {
			setIsLoading(false)
		}
	}

	//Переход на шаг sms и запрос signed_data (/login или /registration)
	const requestSmsCode = async () => {
		if (!phone) return

		setStep('sms')
		setSmsCode('')
		setSmsCodeError('')
		setIsLoading(true)

		try {
			setSignInData(await requestSignedData(phone, true))
			startCooldown(COOLDOWN_CODE_SEC)
		} catch (error) {
			setSmsCodeError(getAxiosErrorMessage(error, 'Не удалось отправить SMS-код. Попробуйте ещё раз'))
		} finally {
			setIsLoading(false)
		}
	}

	//Ввод кода и запрос sms/verify. При успехе persist токенов
	const submitCode = async (code: string) => {
		if (!signInData?.SIGNED_DATA?.length || !phone) return

		setIsLoading(true)

		try {
			const result = await AuthByPhoneApi.verifySms({
				code,
				signedData: signInData.SIGNED_DATA,
				uri: window.location.href
			})

			if (result.status === 'fatal') {
				setIsAuthError(true)
				return
			}
			if (result.status === 'wrong_code' || result.status === 'network') {
				setSmsCodeError(result.message)
				return
			}

			persist(result.tokens, phone)
		} finally {
			setIsLoading(false)
		}
	}

	//Ввод кода, на полной длине (в зависимости от шага) — сабмит
	const onCodeChange = (code: string) => {
		const length = step === 'push' ? 6 : 4
		const value = allowOnlyNumbers(code)
		setSmsCode(value)
		if (smsCodeError) setSmsCodeError('')
		if (value.length === length) submitCode(value)
	}

	//Назад к шагу ввода номера. Абортит висящий call/verify ожидающий звонка
	const goToPhone = () => {
		verifyCallRequestRef.current?.abort()
		captchaRef.current = undefined
		intentRef.current = 'login'
		setSignInData(null)
		setSmsCode('')
		setSmsCodeError('')
		setSubmitError('')
		setStep('phone')
	}

	//Абортит call/verify при размонтировании
	useEffect(() => {
		return () => verifyCallRequestRef.current?.abort()
	}, [])

	//Код из web-push: тост, подстановка в инпут, сабмит кода
	useEffect(() => {
		const authCode = notification?.data?.auth_code
		if (!authCode || !signInData) return

		callNotification.info(`Код авторизации ${authCode} `)
		setSmsCode(authCode)
		clearNotification()
		submitCode(authCode)
	}, [notification, signInData])

	return {
		step,
		phone,
		smsCode,
		smsCodeError,
		submitError,
		isAuthError,
		resendCooldown,
		isCooldown,
		isNewsletterAccepted,
		showNewsletterCheckbox,
		isNewUser: signInData?.IS_NEW_USER,
		isLoading: isAssentLoading || isLoading,
		setIsNewsletterAccepted,
		startLogin,
		confirmRegistration,
		requestSmsCode,
		onCodeChange,
		goToPhone
	}
}
